import type { Unit } from '@/lib/ruler/types/unit'
import { formatInchTickLabel, formatRulerTickNumber } from './ruler-tick-labels'

/**
 * Get zoom-aware tick intervals based on scale level (in points).
 * Pica: minor 1 pica, mid 3 picas, major 6 picas (every inch).
 * Other units use minor + major only (mid = major for two-level).
 */
export function getZoomAwareIntervals(unit: Unit, scale: number) {
  // Base intervals in points. Pica: 1 pica=12pt, 3 picas=36pt, 6 picas=72pt (1 inch)
  const baseConfigs: Record<Unit, { minor: number; mid: number; major: number }> = {
    pica: { minor: 12, mid: 36, major: 72 },
    point: { minor: 6, mid: 36, major: 72 },
    inch: { minor: 72 / 12, mid: 36, major: 72 },
    mm: { minor: 72 / 25.4, mid: (72 / 25.4) * 10, major: (72 / 25.4) * 10 },
  }

  const base = baseConfigs[unit]
  let minor: number
  let mid: number
  let major: number

  if (scale >= 2) {
    minor = base.minor / 2
    mid = base.mid / 2
    major = base.major / 2
  } else if (scale >= 1) {
    ;({ minor, mid, major } = base)
  } else if (scale >= 0.5) {
    minor = base.minor * 2
    mid = base.mid * 2
    major = base.major * 2
  } else if (scale >= 0.25) {
    minor = base.minor * 4
    mid = base.mid * 4
    major = base.major * 4
  } else {
    minor = base.minor * 8
    mid = base.mid * 8
    major = base.major * 8
  }

  return { minor, mid, major }
}

/**
 * Unit configuration for ruler tick generation (minor, mid, major in points)
 */
export function getUnitConfig(unit: Unit, scale: number) {
  const intervals = getZoomAwareIntervals(unit, scale)

  switch (unit) {
    case 'pica':
      return {
        minor: intervals.minor,
        mid: intervals.mid,
        major: intervals.major,
        label: (v: number) => Math.round(v / 12).toString(),
      }
    case 'point':
      return {
        minor: intervals.minor,
        mid: intervals.mid,
        major: intervals.major,
        label: (v: number) => formatRulerTickNumber(v, 2),
      }
    case 'inch':
      return {
        minor: intervals.minor,
        mid: intervals.mid,
        major: intervals.major,
        label: formatInchTickLabel,
      }
    case 'mm':
      return {
        minor: intervals.minor,
        mid: intervals.mid,
        major: intervals.major,
        label: (v: number) => Math.round((v * 25.4) / 72).toString(),
      }
  }
}
